<template>
    <div class="flex flex-col dark:text-white">
        <div class="my-5 flex items-center justify-between">
            <div>
                <router-link
                    :to="{ name: 'fdic.submissions' }"
                    class="text-sm text-indigo-500 hover:underline"
                >
                    &larr; Voltar para cedentes
                </router-link>
                <h1 class="mt-1 text-2xl font-semibold">
                    Histórico FDIC —
                    <span
                        v-if="userName"
                        class="text-gray-600 dark:text-gray-300"
                        >{{ userName }}</span
                    >
                    <span v-else class="text-gray-500"
                        >#{{ $route.params.userId }}</span
                    >
                </h1>
            </div>
        </div>

        <div v-if="loading" class="text-sm text-gray-500">Carregando...</div>

        <div
            v-else
            class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
        >
            <table
                class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
            >
                <thead class="bg-gray-50 dark:bg-gray-800">
                    <tr>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            ID
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            Tipo
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            Status
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            Origem
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            Ticket
                        </th>
                        <th
                            class="px-4 py-3 text-left text-xs font-semibold uppercase"
                        >
                            Enviado em
                        </th>
                        <th class="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody
                    class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900"
                >
                    <tr v-for="item in items" :key="item.id">
                        <td class="px-4 py-3 text-sm text-gray-500">
                            {{ item.id }}
                        </td>
                        <td class="px-4 py-3 text-sm">{{ item.type }}</td>
                        <td class="px-4 py-3 text-sm">
                            <span
                                :class="statusClass(item.status)"
                                class="rounded-full px-2 py-1 text-xs font-semibold"
                            >
                                {{ statusLabel(item.status) }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <span
                                v-if="item.source"
                                :class="sourceClass(item.source)"
                                class="rounded-full px-2 py-1 text-xs font-semibold"
                            >
                                {{ sourceLabel(item.source) }}
                            </span>
                            <span v-else class="text-gray-500">—</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-500">
                            {{ item.ticket || "—" }}
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-500">
                            {{ formatDate(item.submitted_at) }}
                        </td>
                        <td class="px-4 py-3 text-right text-sm">
                            <router-link
                                :to="{
                                    name: 'fdic.detail',
                                    params: { id: item.id },
                                    query: {
                                        from: 'history',
                                        userId: $route.params.userId,
                                    },
                                }"
                                class="text-indigo-500 hover:underline"
                                >Ver detalhes</router-link
                            >
                        </td>
                    </tr>
                    <tr v-if="!items.length">
                        <td
                            colspan="7"
                            class="px-4 py-6 text-center text-sm text-gray-500"
                        >
                            Nenhum envio encontrado.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div
            v-if="pagination.last_page > 1"
            class="mt-4 flex items-center justify-between"
        >
            <button
                :disabled="pagination.current_page <= 1"
                @click="fetch(pagination.current_page - 1)"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:bg-gray-800"
            >
                Anterior
            </button>
            <span class="text-sm"
                >Página {{ pagination.current_page }} de
                {{ pagination.last_page }}</span
            >
            <button
                :disabled="pagination.current_page >= pagination.last_page"
                @click="fetch(pagination.current_page + 1)"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:bg-gray-800"
            >
                Próximo
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: "FdicSubmissionHistory",
    data() {
        return {
            loading: false,
            items: [],
            pagination: {
                current_page: 1,
                last_page: 1,
            },
        };
    },
    computed: {
        userName() {
            const first = this.items[0];
            return first?.user?.name || first?.user?.email || null;
        },
    },
    mounted() {
        this.fetch(1);
    },
    methods: {
        async fetch(page = 1) {
            this.loading = true;
            try {
                const { data } = await window.axios.get(
                    `/api/fdic/submissions/user/${this.$route.params.userId}/history`,
                    { params: { page, per_page: 50 } },
                );
                this.items = data.data ?? data;
                this.pagination.current_page = data.current_page ?? page;
                this.pagination.last_page = data.last_page ?? 1;
            } finally {
                this.loading = false;
            }
        },
        formatDate(iso) {
            if (!iso) return "—";
            return new Date(iso).toLocaleString("pt-BR");
        },
        statusLabel(status) {
            const map = {
                pending: "Pendente",
                processing: "Processando",
                approved: "Aprovado",
                approved_simple: "Aprovado Simples",
                approved_with_warnings: "Aprovado c/ Alertas",
                rejected: "Rejeitado",
                error: "Erro",
            };
            return map[status] ?? status;
        },
        statusClass(status) {
            return {
                "bg-yellow-100 text-yellow-800":
                    status === "pending" || status === "processing",
                "bg-green-100 text-green-800":
                    status === "approved" || status === "approved_simple",
                "bg-orange-100 text-orange-800":
                    status === "approved_with_warnings",
                "bg-red-100 text-red-800":
                    status === "rejected" || status === "error",
            };
        },
        sourceLabel(source) {
            const map = {
                first_sale: "Primeira Venda",
                idwall: "Idwall",
            };
            return map[source] ?? source;
        },
        sourceClass(source) {
            return {
                "bg-indigo-100 text-indigo-800": source === "first_sale",
                "bg-sky-100 text-sky-800": source === "idwall",
                "bg-gray-100 text-gray-800":
                    source !== "first_sale" && source !== "idwall",
            };
        },
    },
};
</script>
